import React, { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CaretDown, CaretRight, ArrowsClockwise, Baseball, Basketball,
  Globe, ChartLineUp, Bookmark, Trophy, Plus, Check,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import TeamLogo from "./TeamLogo";

const TODAY_ISO = () => new Date().toISOString().slice(0, 10);

const CHARLIE_BLUE = "#007AFF";
const DAKOTA_PURPLE = "#A855F7";
const GREEN = "#22C55E";
const RED = "#FF3B30";
const YELLOW = "#F59E0B";

const fmtPct = (n) => `${((n || 0) * 100).toFixed(1)}%`;
const fmtMoney = (n) => {
  const v = Number(n || 0);
  const sign = v >= 0 ? "+" : "−";
  return `${sign}$${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};
const moneyColor = (n) => (n > 0 ? GREEN : n < 0 ? RED : "#a1a1aa");
const wrColor = (wr, picks) => {
  if (!picks) return "#52525b";
  if (wr >= 0.6) return GREEN;
  if (wr >= 0.5) return YELLOW;
  return RED;
};

const VIEW_META = {
  universe: {
    key: "universe",
    letter: "A",
    label: "Universe",
    desc: "Applied to every team — all rules + Charlie/Dakota collision skip",
    Icon: Globe,
  },
  above_500: {
    key: "above_500",
    letter: "B",
    label: "Above .500 (rolling)",
    desc: "Only triggers where the team's record up to that day was above .500",
    Icon: ChartLineUp,
  },
  selected: {
    key: "selected",
    letter: "C",
    label: "Selected",
    desc: "Teams currently in your shortlist",
    Icon: Bookmark,
  },
};

function SummaryStrip({ summary, accent }) {
  const picks = summary?.picks || 0;
  const wins = summary?.wins || 0;
  const losses = summary?.losses || 0;
  const skipped = summary?.skipped || 0;
  return (
    <div className="grid grid-cols-4 gap-px bg-white/5 border border-white/10 rounded-sm overflow-hidden text-xs">
      <div className="bg-[#0A0A0A] px-3 py-2">
        <div className="text-[9px] uppercase tracking-widest text-zinc-500">Picks</div>
        <div className="font-display text-lg" style={{ color: accent }}>{picks}</div>
      </div>
      <div className="bg-[#0A0A0A] px-3 py-2">
        <div className="text-[9px] uppercase tracking-widest text-zinc-500">W − L</div>
        <div className="font-mono-d text-base mt-0.5">{wins}-{losses}{skipped ? <span className="text-zinc-600 text-[10px] ml-1">·{skipped}sk</span> : null}</div>
      </div>
      <div className="bg-[#0A0A0A] px-3 py-2">
        <div className="text-[9px] uppercase tracking-widest text-zinc-500">Win %</div>
        <div className="font-mono-d text-base mt-0.5 font-bold" style={{ color: wrColor(summary?.win_rate, picks) }}>
          {picks ? fmtPct(summary.win_rate) : "—"}
        </div>
      </div>
      <div className="bg-[#0A0A0A] px-3 py-2">
        <div className="text-[9px] uppercase tracking-widest text-zinc-500">P/L</div>
        <div className="font-mono-d text-base mt-0.5 font-bold" style={{ color: moneyColor(summary?.profit) }}>
          {picks ? fmtMoney(summary?.profit) : "—"}
        </div>
      </div>
    </div>
  );
}

function BetLogRow({ row }) {
  const isWin = row.result === "W";
  const isLoss = row.result === "L";
  const isSkip = (row.result || "").startsWith("SKIP");
  const badge = isWin ? "bg-[#22C55E] text-black"
    : isLoss ? "bg-[#FF3B30] text-black"
    : isSkip ? "bg-yellow-400/15 text-yellow-400 border border-yellow-400/30"
    : "bg-zinc-700 text-zinc-300";
  return (
    <tr className="border-b border-white/5 text-[11px]" data-testid={`bet-log-row-${row.target_date}`}>
      <td className="px-2 py-1 font-mono-d text-zinc-400">{row.target_date}</td>
      <td className="px-2 py-1 text-zinc-300">
        {row.is_home === true ? "vs" : row.is_home === false ? "@" : ""} {row.opponent_abbr || row.opponent || "—"}
      </td>
      <td className="px-2 py-1 font-mono-d text-zinc-500">{row.score || "—"}</td>
      <td className="px-2 py-1 text-zinc-500 text-[10px]">
        {row.trigger_n ? `#${row.trigger_n}` : ""}{" "}
        {row.trigger_reason || row.reason || ""}
        {row.skipped_reason ? <span className="text-yellow-500 ml-1">· {row.skipped_reason}</span> : ""}
      </td>
      <td className="px-2 py-1 text-right">
        <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm font-bold ${badge}`}>{row.result || "—"}</span>
      </td>
      <td className="px-2 py-1 text-right font-mono-d" style={{ color: moneyColor(row.profit) }}>
        {typeof row.profit === "number" && row.profit !== 0 ? fmtMoney(row.profit) : "—"}
      </td>
    </tr>
  );
}

function TeamRow({ team, accent, expanded, onToggle, sport, isShortlisted, onShortlist, busyShortlist }) {
  const picks = team.picks || 0;
  const handleShortlist = (e) => {
    e.stopPropagation();
    if (busyShortlist) return;
    onShortlist?.(team, !isShortlisted);
  };
  return (
    <>
      <tr
        className="border-b border-white/5 hover:bg-white/[0.03] cursor-pointer transition-colors"
        onClick={onToggle}
        data-testid={`matrix-team-row-${team.abbr}`}
      >
        <td className="px-3 py-2 w-6">
          {expanded ? <CaretDown size={12} className="text-zinc-400" /> : <CaretRight size={12} className="text-zinc-500" />}
        </td>
        <td className="px-2 py-2">
          <div className="flex items-center gap-2">
            <TeamLogo sport={sport} abbr={team.abbr} size={20} />
            <span className="font-bold text-sm">{team.abbr}</span>
            {onShortlist && (
              <button
                onClick={handleShortlist}
                disabled={busyShortlist}
                data-testid={`shortlist-toggle-${sport}-${team.abbr}`}
                title={isShortlisted ? "Remove from shortlist (effective today)" : "Add to shortlist (effective today)"}
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] uppercase tracking-wider border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                  isShortlisted
                    ? "border-[#22C55E]/50 text-[#22C55E] bg-[#22C55E]/10 hover:bg-[#22C55E]/20"
                    : "border-white/15 text-zinc-400 hover:border-white/40 hover:text-white"
                }`}
              >
                {isShortlisted ? <Check size={10} weight="bold" /> : <Plus size={10} weight="bold" />}
                {isShortlisted ? "Shortlisted" : "Shortlist"}
              </button>
            )}
          </div>
        </td>
        <td className="px-2 py-2 text-right font-mono-d text-sm">{picks}</td>
        <td className="px-2 py-2 text-right font-mono-d text-xs text-zinc-300">
          {picks ? `${team.wins}-${team.losses}` : "—"}
          {team.skipped ? <span className="text-zinc-600 text-[10px] ml-1">·{team.skipped}sk</span> : null}
        </td>
        <td className="px-2 py-2 text-right font-mono-d text-sm font-bold" style={{ color: wrColor(team.win_rate, picks) }}>
          {picks ? fmtPct(team.win_rate) : "—"}
        </td>
        <td className="px-3 py-2 text-right font-mono-d text-sm font-bold" style={{ color: moneyColor(team.profit) }}>
          {picks ? fmtMoney(team.profit) : "—"}
        </td>
        <td className="px-2 py-2 text-right font-mono-d text-[10px] text-zinc-500">{team.season_record || "—"}</td>
      </tr>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.tr
            key={`exp-${team.abbr}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            <td colSpan={7} className="bg-black/40 px-2 py-2" style={{ borderLeft: `2px solid ${accent}` }}>
              {team.bet_log && team.bet_log.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-[11px]">
                    <thead className="text-[9px] uppercase tracking-widest text-zinc-500 border-b border-white/10">
                      <tr>
                        <th className="text-left px-2 py-1">Target date</th>
                        <th className="text-left px-2 py-1">Opponent</th>
                        <th className="text-left px-2 py-1">Score</th>
                        <th className="text-left px-2 py-1">Trigger</th>
                        <th className="text-right px-2 py-1">Result</th>
                        <th className="text-right px-2 py-1">P/L</th>
                      </tr>
                    </thead>
                    <tbody>
                      {team.bet_log.map((row, i) => <BetLogRow key={i} row={row} />)}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-xs text-zinc-500 italic px-2 py-1">No detailed pick records for this team.</div>
              )}
            </td>
          </motion.tr>
        )}
      </AnimatePresence>
    </>
  );
}

function ViewSection({ view, viewMeta, accent, analyst, sportKey, defaultOpen, selectedKeys, optimisticOverrides, onShortlist, busyShortlist }) {
  const [open, setOpen] = useState(defaultOpen);
  const [expandedTeam, setExpandedTeam] = useState(null);
  const { Icon, letter, label, desc } = viewMeta;
  const teams = view?.teams || [];
  const summary = view?.summary || {};
  return (
    <div
      className="bg-[#0F0F0F] border border-white/10 rounded-sm overflow-hidden"
      data-testid={`matrix-${sportKey}-${analyst}-${viewMeta.key}`}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-white/[0.02] transition-colors text-left"
      >
        <div
          className="w-7 h-7 rounded-sm flex items-center justify-center font-display text-xs"
          style={{ color: accent, background: `${accent}1A`, border: `1px solid ${accent}44` }}
        >
          {letter}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Icon size={12} weight="fill" style={{ color: accent }} />
            <div className="font-display text-sm uppercase tracking-wider">{label}</div>
          </div>
          <div className="text-[10px] text-zinc-500 mt-0.5 truncate">{desc}</div>
        </div>
        {open ? <CaretDown size={14} className="text-zinc-500" /> : <CaretRight size={14} className="text-zinc-500" />}
      </button>

      <div className="px-3 pb-3">
        <SummaryStrip summary={summary} accent={accent} />
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="overflow-hidden"
          >
            <div className="border-t border-white/5 overflow-x-auto">
              {teams.length === 0 ? (
                <div className="text-xs text-zinc-500 italic px-4 py-4 text-center">
                  No teams qualify under this view.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-[9px] uppercase tracking-widest text-zinc-500 border-b border-white/5">
                    <tr>
                      <th className="w-6 px-3 py-1.5" />
                      <th className="text-left px-2 py-1.5">Team</th>
                      <th className="text-right px-2 py-1.5">Picks</th>
                      <th className="text-right px-2 py-1.5">W-L</th>
                      <th className="text-right px-2 py-1.5">Win %</th>
                      <th className="text-right px-3 py-1.5">P/L</th>
                      <th className="text-right px-2 py-1.5">Season</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teams.map((t) => {
                      const key = sportKey === "mlb" ? t.team_id : (t.abbr || t.team_id);
                      const baseSelected = !!selectedKeys?.has(key);
                      const optimisticKey = `${analyst}:${key}`;
                      const override = optimisticOverrides ? optimisticOverrides[optimisticKey] : undefined;
                      const isShortlisted = typeof override === "boolean" ? override : baseSelected;
                      return (
                        <TeamRow
                          key={t.abbr}
                          team={t}
                          accent={accent}
                          sport={sportKey}
                          expanded={expandedTeam === t.abbr}
                          onToggle={() => setExpandedTeam(expandedTeam === t.abbr ? null : t.abbr)}
                          isShortlisted={isShortlisted}
                          onShortlist={onShortlist}
                          busyShortlist={busyShortlist}
                        />
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function AnalystColumn({ analystKey, accent, label, sportKey, sportData, selectedKeys, optimisticOverrides, onShortlist, busyShortlist }) {
  const views = sportData?.views || {};
  return (
    <div className="space-y-3" data-testid={`matrix-${sportKey}-col-${analystKey}`}>
      <div className="flex items-baseline gap-2 px-1">
        <div className="font-display text-2xl" style={{ color: accent }}>{label}</div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">{sportKey === "mlb" ? "MLB" : "WNBA"}</div>
      </div>
      <ViewSection
        view={views.universe?.[analystKey]}
        viewMeta={VIEW_META.universe}
        accent={accent}
        analyst={analystKey}
        sportKey={sportKey}
        defaultOpen={true}
        selectedKeys={selectedKeys}
        optimisticOverrides={optimisticOverrides}
        onShortlist={onShortlist}
        busyShortlist={busyShortlist}
      />
      <ViewSection
        view={views.above_500?.[analystKey]}
        viewMeta={VIEW_META.above_500}
        accent={accent}
        analyst={analystKey}
        sportKey={sportKey}
        defaultOpen={false}
        selectedKeys={selectedKeys}
        optimisticOverrides={optimisticOverrides}
        onShortlist={onShortlist}
        busyShortlist={busyShortlist}
      />
      <ViewSection
        view={views.selected?.[analystKey]}
        viewMeta={VIEW_META.selected}
        accent={accent}
        analyst={analystKey}
        sportKey={sportKey}
        defaultOpen={false}
        selectedKeys={selectedKeys}
        optimisticOverrides={optimisticOverrides}
        onShortlist={onShortlist}
        busyShortlist={busyShortlist}
      />
    </div>
  );
}

function PerTeamFocusTable({ teams, sport, analystKey, accent, selectedKeys, optimisticOverrides, onShortlist, busyShortlist }) {
  // Derive per-team Last Pick result + Last 10 sequence from the team.bet_log.
  // bet_log is chronological (oldest → newest) and includes W, L, and SKIP-* rows.
  // We filter to only placed bets, then take the most recent ones.
  const enrich = (t) => {
    const placed = (t.bet_log || []).filter((b) => b.result === "W" || b.result === "L");
    const totalStake = placed.reduce((s, b) => s + (Number(b.stake) || 0), 0);
    const roi = totalStake > 0 ? (Number(t.profit || 0) / totalStake) * 100 : 0;
    const newestFirst = [...placed].reverse();
    return {
      lastResult: newestFirst[0]?.result || null,
      last10: newestFirst.slice(0, 10).map((b) => b.result),
      totalStake,
      roi,
    };
  };

  if (!teams || teams.length === 0) {
    return (
      <div className="bg-[#121212] border border-white/10 rounded-sm px-4 py-8 text-center text-xs text-zinc-500 italic" data-testid="per-team-empty">
        No teams with picks for {analystKey === "charlie" ? "Charlie" : "Dakota"} in this universe.
      </div>
    );
  }

  return (
    <div className="bg-[#121212] border border-white/10 rounded-sm overflow-x-auto" data-testid={`per-team-table-${sport}-${analystKey}`}>
      <table className="w-full text-sm min-w-[860px]">
        <thead>
          <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/10">
            <th className="text-left px-3 py-2.5">Team</th>
            <th className="text-left px-2 py-2.5">Season W-L</th>
            <th className="text-left px-2 py-2.5">Picks (W-L)</th>
            <th className="text-right px-2 py-2.5">$$$ Made</th>
            <th className="text-right px-2 py-2.5">ROI %</th>
            <th className="text-center px-2 py-2.5">Last</th>
            <th className="text-left px-3 py-2.5">Last 10</th>
            <th className="text-right px-3 py-2.5">Shortlist</th>
          </tr>
        </thead>
        <tbody>
          {teams.map((t) => {
            const e = enrich(t);
            const key = sport === "mlb" ? t.team_id : (t.abbr || t.team_id);
            const baseSelected = !!selectedKeys?.has(key);
            const optKey = `${analystKey}:${key}`;
            const override = optimisticOverrides ? optimisticOverrides[optKey] : undefined;
            const isShortlisted = typeof override === "boolean" ? override : baseSelected;
            return (
              <tr key={t.abbr} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`per-team-row-${t.abbr}`}>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <TeamLogo sport={sport} abbr={t.abbr} size={22} />
                    <span className="font-bold text-sm">{t.abbr}</span>
                  </div>
                </td>
                <td className="px-2 py-2 font-mono-d text-xs text-zinc-300">{t.season_record || "—"}</td>
                <td className="px-2 py-2 font-mono-d text-xs">
                  <span className="text-[#22C55E]">{t.wins}</span>
                  <span className="text-zinc-500">-</span>
                  <span className="text-[#FF3B30]">{t.losses}</span>
                </td>
                <td className="px-2 py-2 font-mono-d text-xs text-right" style={{ color: moneyColor(t.profit) }}>
                  {fmtMoney(t.profit)}
                </td>
                <td className="px-2 py-2 font-mono-d text-xs text-right" style={{ color: moneyColor(e.roi) }}>
                  {e.roi >= 0 ? "+" : ""}{e.roi.toFixed(1)}%
                </td>
                <td className="px-2 py-2 text-center">
                  {e.lastResult ? (
                    <span className={`inline-block w-5 h-5 leading-5 text-[10px] font-bold rounded-sm ${
                      e.lastResult === "W" ? "bg-[#22C55E]/20 text-[#22C55E]" : "bg-[#FF3B30]/20 text-[#FF3B30]"
                    }`}>{e.lastResult}</span>
                  ) : <span className="text-zinc-600">—</span>}
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-0.5 font-mono-d text-[10px]" title="Newest → oldest">
                    {e.last10.length === 0 ? (
                      <span className="text-zinc-600">—</span>
                    ) : (
                      e.last10.map((r, i) => (
                        <span
                          key={i}
                          className={`inline-block w-4 h-4 leading-4 text-center font-bold rounded-sm ${
                            r === "W" ? "bg-[#22C55E]/20 text-[#22C55E]" : "bg-[#FF3B30]/20 text-[#FF3B30]"
                          }`}
                        >{r}</span>
                      ))
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    onClick={() => !busyShortlist && onShortlist?.(t, !isShortlisted)}
                    disabled={busyShortlist}
                    data-testid={`per-team-shortlist-${sport}-${t.abbr}`}
                    title={isShortlisted ? `Remove ${t.abbr} from ${analystKey === "charlie" ? "Charlie" : "Dakota"}'s shortlist` : `Add ${t.abbr} to ${analystKey === "charlie" ? "Charlie" : "Dakota"}'s shortlist`}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] uppercase tracking-widest font-bold border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                      isShortlisted
                        ? "border-[#22C55E]/50 text-[#22C55E] bg-[#22C55E]/10 hover:bg-[#22C55E]/20"
                        : "border-white/15 text-zinc-400 hover:border-white/40 hover:text-white"
                    }`}
                    style={!isShortlisted ? { borderColor: `${accent}40`, color: accent } : undefined}
                  >
                    {isShortlisted ? <Check size={11} weight="bold" /> : <Plus size={11} weight="bold" />}
                    {isShortlisted ? "Shortlisted" : "Add"}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function StrategyMatrixSection({ mlbActiveTeams }) {
  // mlbActiveTeams = { iso: [...team ids], wc: [...team ids], allTeams: [{id, abbr, name}] }
  const [sport, setSport] = useState("mlb"); // "mlb" | "wnba"
  const [season, setSeason] = useState({ mlb: 2026, wnba: 2025 });
  const [mlbData, setMlbData] = useState(null);
  const [wnbaData, setWnbaData] = useState(null);
  const [wnbaSelChar, setWnbaSelChar] = useState(""); // comma-separated abbrs
  const [wnbaSelDak, setWnbaSelDak] = useState("");
  const [loading, setLoading] = useState(false);
  const [busyShortlist, setBusyShortlist] = useState(false);
  // NEW: drill-down flow — admin picks ONE analyst at a time to focus on.
  // The flat per-team table + A/B/C $ comparison both honour this choice.
  // The side-by-side Charlie vs Dakota grid moves to a collapsed bottom section.
  const [focusAnalyst, setFocusAnalyst] = useState("charlie"); // "charlie" | "dakota"
  const [compareOpen, setCompareOpen] = useState(false);
  // Optimistic overrides: key -> bool (true = appears shortlisted, false = not).
  // Keyed by `${analystKey}:${teamKey}` so MLB/WNBA + both analysts stay isolated.
  // Cleared once the matrix refresh resolves (server is the source of truth then).
  const [optimisticShortlist, setOptimisticShortlist] = useState({});

  const sportData = sport === "mlb" ? mlbData : wnbaData;

  const loadMlb = async (refresh = false) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/strategy/comparison-matrix?season=${season.mlb}${refresh ? "&refresh=true" : ""}`);
      setMlbData(data);
      if (refresh) toast.success("MLB matrix refreshed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load MLB matrix");
    } finally { setLoading(false); }
  };

  const loadWnba = async (refresh = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ season: String(season.wnba) });
      if (wnbaSelChar) params.set("selected_charlie", wnbaSelChar);
      if (wnbaSelDak) params.set("selected_dakota", wnbaSelDak);
      if (refresh) params.set("refresh", "true");
      const { data } = await api.get(`/admin/strategy/wnba-matrix?${params.toString()}`);
      setWnbaData(data);
      if (refresh) toast.success("WNBA matrix refreshed from ESPN");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load WNBA matrix");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (sport === "mlb" && !mlbData) loadMlb(false);
    if (sport === "wnba" && !wnbaData) loadWnba(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sport]);

  useEffect(() => {
    if (sport === "mlb") loadMlb(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [season.mlb]);

  useEffect(() => {
    if (sport === "wnba") loadWnba(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [season.wnba]);

  // Shortlist key sets — what the matrix considers currently "selected" for each analyst.
  // MLB: server-side persistent set keyed by team_id. WNBA: local input-driven set keyed by abbr.
  const selectedKeysCharlie = useMemo(() => {
    if (sport === "mlb") return new Set(mlbData?.selected_teams?.iso || []);
    return new Set(wnbaData?.selected_teams?.charlie || []);
  }, [sport, mlbData, wnbaData]);

  const selectedKeysDakota = useMemo(() => {
    if (sport === "mlb") return new Set(mlbData?.selected_teams?.wc || []);
    return new Set(wnbaData?.selected_teams?.dakota || []);
  }, [sport, mlbData, wnbaData]);

  // Toggle Charlie / Dakota shortlist for a team directly from the matrix row.
  // MLB: hits the persistent /admin/strategy/teams endpoint with effective_date=today
  //      so the matrix simulation immediately picks it up on refresh.
  // WNBA: updates local comma-separated input state and re-queries the matrix.
  const toggleShortlist = async (analystKey, team, add) => {
    if (busyShortlist) return;
    const teamKey = sport === "mlb" ? team.team_id : (team.abbr || team.team_id);
    const optimisticKey = `${analystKey}:${teamKey}`;
    // Paint optimistic state immediately — keeps button feel snappy during 2-5s
    // matrix refresh.
    setOptimisticShortlist((prev) => ({ ...prev, [optimisticKey]: add }));
    setBusyShortlist(true);
    try {
      if (sport === "mlb") {
        const strategy = analystKey === "charlie" ? "iso" : "wc";
        const { data: resp } = await api.post("/admin/strategy/teams", {
          strategy,
          action: add ? "add" : "remove",
          team_id: team.team_id,
          effective_date: TODAY_ISO(),
        });
        const newly = resp?.relocked?.newly_locked || 0;
        const lockedMsg = add && newly > 0 ? ` · ${newly} new pick${newly === 1 ? "" : "s"} locked for today` : add && resp?.relocked?.triggered ? " · today's slate unchanged (no new triggers)" : "";
        toast.success(`${team.abbr} ${add ? "added to" : "removed from"} ${analystKey === "charlie" ? "Charlie" : "Dakota"}'s shortlist${lockedMsg}`);
        await loadMlb(true);
      } else {
        const abbr = (team.abbr || "").toUpperCase();
        const updater = analystKey === "charlie" ? setWnbaSelChar : setWnbaSelDak;
        const current = analystKey === "charlie" ? wnbaSelChar : wnbaSelDak;
        const set = new Set(current.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean));
        if (add) set.add(abbr); else set.delete(abbr);
        const next = Array.from(set).join(",");
        updater(next);
        // Also persist to the WNBA shortlist backend so today's slate gets
        // re-locked. (Local input state is for the matrix sim, persisted state
        // is for live picks.)
        let lockedMsg = "";
        try {
          const { data: resp } = await api.post("/admin/strategy/wnba-teams", {
            strategy: analystKey,            // "charlie" | "dakota"
            action: add ? "add" : "remove",
            team_abbr: abbr,
            effective_date: TODAY_ISO(),
          });
          const newly = resp?.relocked?.newly_locked || 0;
          lockedMsg = add && newly > 0 ? ` · ${newly} new pick${newly === 1 ? "" : "s"} locked for today` : add && resp?.relocked?.triggered ? " · today's slate unchanged (no new triggers)" : "";
        } catch (persistErr) {
          // Persistence failure shouldn't block the matrix sim — log only.
          // eslint-disable-next-line no-console
          console.warn("WNBA shortlist persist failed", persistErr);
        }
        toast.success(`${abbr} ${add ? "added to" : "removed from"} ${analystKey === "charlie" ? "Charlie" : "Dakota"}'s shortlist${lockedMsg}`);
        await loadWnbaWithOverrides(
          analystKey === "charlie" ? next : wnbaSelChar,
          analystKey === "dakota" ? next : wnbaSelDak,
        );
      }
    } catch (e) {
      // Revert optimistic state on error.
      setOptimisticShortlist((prev) => {
        const { [optimisticKey]: _, ...rest } = prev;
        return rest;
      });
      toast.error(e.response?.data?.detail || "Couldn't update shortlist");
    } finally {
      // Clear this particular optimistic entry now that the server response is
      // committed (selectedKeys is recomputed from the fresh sportData).
      setOptimisticShortlist((prev) => {
        const { [optimisticKey]: _, ...rest } = prev;
        return rest;
      });
      setBusyShortlist(false);
    }
  };

  const loadWnbaWithOverrides = async (selChar, selDak) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ season: String(season.wnba) });
      if (selChar) params.set("selected_charlie", selChar);
      if (selDak) params.set("selected_dakota", selDak);
      params.set("refresh", "true");
      const { data } = await api.get(`/admin/strategy/wnba-matrix?${params.toString()}`);
      setWnbaData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load WNBA matrix");
    } finally { setLoading(false); }
  };

  const selectedTeamsLabel = useMemo(() => {
    if (sport === "mlb" && mlbData?.selected_teams) {
      const map = {};
      (mlbActiveTeams?.allTeams || []).forEach((t) => { map[t.id] = t.abbr; });
      const iso = mlbData.selected_teams.iso.map((id) => map[id] || id).join(", ");
      const wc = mlbData.selected_teams.wc.map((id) => map[id] || id).join(", ");
      return `Charlie: ${iso || "—"} · Dakota: ${wc || "—"}`;
    }
    if (sport === "wnba" && wnbaData?.selected_teams) {
      const c = wnbaData.selected_teams.charlie.join(", ") || "—";
      const d = wnbaData.selected_teams.dakota.join(", ") || "—";
      return `Charlie: ${c} · Dakota: ${d}`;
    }
    return "";
  }, [sport, mlbData, wnbaData, mlbActiveTeams]);

  return (
    <motion.section
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      className="mt-12 pt-10 border-t border-white/10"
      data-testid="strategy-matrix-section"
    >
      <div className="mb-5">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#22C55E] mb-1">Admin · Signal Lab</div>
        <h2 className="font-display text-4xl sm:text-5xl leading-none">COMPARISON MATRIX</h2>
        <p className="text-sm text-zinc-400 max-w-3xl mt-3 leading-relaxed">
          Drill in: pick a <span className="text-white font-bold">league</span>, pick an <span className="text-white font-bold">analyst</span>,
          then see every team's pick record under that strategy. Add any team to the shortlist inline,
          then compare the <span className="text-white font-bold">A / B / C $$$</span> for that analyst. The classic side-by-side
          Charlie vs Dakota view is collapsed at the bottom of this section.
          <span className="text-zinc-500 italic"> P/L assumes the standard $250-target / -170 flat sizing.</span>
        </p>
      </div>

      {/* Sport tabs + controls */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div className="inline-flex border-2 border-white/20 rounded-sm overflow-hidden shadow-lg" data-testid="matrix-sport-tabs">
          <button
            onClick={() => setSport("mlb")}
            data-testid="matrix-sport-mlb"
            className={`inline-flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-widest font-bold transition-all ${
              sport === "mlb"
                ? "bg-[#007AFF] text-white shadow-[inset_0_-3px_0_0_rgba(0,0,0,0.35)]"
                : "bg-[#007AFF]/15 text-[#5BA8FF] hover:bg-[#007AFF]/30 hover:text-white"
            }`}
          >
            <Baseball size={15} weight="fill" /> MLB
          </button>
          <button
            onClick={() => setSport("wnba")}
            data-testid="matrix-sport-wnba"
            className={`inline-flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-widest font-bold transition-all border-l-2 border-white/20 ${
              sport === "wnba"
                ? "bg-[#FF6B35] text-white shadow-[inset_0_-3px_0_0_rgba(0,0,0,0.35)]"
                : "bg-[#FF6B35]/15 text-[#FFA985] hover:bg-[#FF6B35]/30 hover:text-white"
            }`}
          >
            <Basketball size={15} weight="fill" /> WNBA
          </button>
        </div>

        <div className="flex items-center gap-2">
          {sport === "mlb" ? (
            <select
              value={season.mlb}
              onChange={(e) => setSeason((s) => ({ ...s, mlb: parseInt(e.target.value, 10) }))}
              data-testid="matrix-mlb-season"
              className="bg-[#0A0A0A] border border-white/15 rounded-sm px-3 py-2 text-xs font-mono-d"
            >
              <option value={2026}>2026 MLB</option>
              <option value={2025}>2025 MLB</option>
              <option value={2024}>2024 MLB</option>
            </select>
          ) : (
            <>
              <select
                value={season.wnba}
                onChange={(e) => setSeason((s) => ({ ...s, wnba: parseInt(e.target.value, 10) }))}
                data-testid="matrix-wnba-season"
                className="bg-[#0A0A0A] border border-white/15 rounded-sm px-3 py-2 text-xs font-mono-d"
              >
                <option value={2026}>2026 WNBA</option>
                <option value={2025}>2025 WNBA</option>
                <option value={2024}>2024 WNBA</option>
              </select>
              <input
                value={wnbaSelChar}
                onChange={(e) => setWnbaSelChar(e.target.value.toUpperCase())}
                onBlur={() => loadWnba(false)}
                placeholder="Charlie WNBA picks (NY,LV…)"
                data-testid="matrix-wnba-sel-char"
                className="bg-[#0A0A0A] border border-white/15 rounded-sm px-3 py-2 text-xs font-mono-d w-44"
              />
              <input
                value={wnbaSelDak}
                onChange={(e) => setWnbaSelDak(e.target.value.toUpperCase())}
                onBlur={() => loadWnba(false)}
                placeholder="Dakota WNBA picks (IND,LV…)"
                data-testid="matrix-wnba-sel-dak"
                className="bg-[#0A0A0A] border border-white/15 rounded-sm px-3 py-2 text-xs font-mono-d w-44"
              />
            </>
          )}
          <button
            onClick={() => (sport === "mlb" ? loadMlb(true) : loadWnba(true))}
            disabled={loading}
            data-testid="matrix-refresh"
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-white/15 hover:border-white/40 text-xs uppercase tracking-widest rounded-sm transition-colors disabled:opacity-40"
          >
            <ArrowsClockwise size={12} className={loading ? "animate-spin" : ""} />
            {loading ? "Loading" : "Refresh"}
          </button>
        </div>
      </div>

      {/* Selected teams banner */}
      <div className="bg-[#121212] border border-white/10 rounded-sm px-4 py-2 mb-5 flex items-center gap-3 text-xs">
        <Trophy size={14} weight="fill" className="text-[#22C55E]" />
        <span className="text-zinc-500 uppercase tracking-widest text-[10px]">Shortlist (C)</span>
        <span className="font-mono-d text-zinc-300 truncate" data-testid="matrix-selected-banner">
          {selectedTeamsLabel || "—"}
        </span>
        {sportData && (
          <span className="ml-auto text-[10px] text-zinc-500 font-mono-d">
            {sport === "wnba" ? `${sportData.games_total} WNBA games` : `MLB ${sportData.season}`}
            {typeof sportData._cache_age_seconds === "number" && (
              <span className="ml-2">· {sportData._cache_age_seconds === 0 ? "Fresh" : `Cached ${sportData._cache_age_seconds}s`}</span>
            )}
          </span>
        )}
      </div>

      {!sportData && loading && (
        <div className="text-zinc-500 font-mono-d text-sm py-12 text-center" data-testid="matrix-loading">
          Running {sport.toUpperCase()} backtests across all 3 views…
        </div>
      )}

      {sportData && (
        <>
          {/* Step 3: Analyst focus toggle */}
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div className="inline-flex border-2 border-white/20 rounded-sm overflow-hidden shadow-lg" data-testid="matrix-focus-tabs">
              <button
                onClick={() => setFocusAnalyst("charlie")}
                data-testid="matrix-focus-charlie"
                className={`inline-flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-widest font-bold transition-all ${
                  focusAnalyst === "charlie"
                    ? "text-white shadow-[inset_0_-3px_0_0_rgba(0,0,0,0.35)]"
                    : "hover:text-white"
                }`}
                style={focusAnalyst === "charlie"
                  ? { backgroundColor: CHARLIE_BLUE }
                  : { backgroundColor: `${CHARLIE_BLUE}26`, color: "#5BA8FF" }}
              >
                Charlie
              </button>
              <button
                onClick={() => setFocusAnalyst("dakota")}
                data-testid="matrix-focus-dakota"
                className={`inline-flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-widest font-bold transition-all border-l-2 border-white/20 ${
                  focusAnalyst === "dakota"
                    ? "text-white shadow-[inset_0_-3px_0_0_rgba(0,0,0,0.35)]"
                    : "hover:text-white"
                }`}
                style={focusAnalyst === "dakota"
                  ? { backgroundColor: DAKOTA_PURPLE }
                  : { backgroundColor: `${DAKOTA_PURPLE}26`, color: "#D8B4FE" }}
              >
                Dakota
              </button>
            </div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500">
              Focused view · {sport === "mlb" ? "MLB" : "WNBA"} · {focusAnalyst === "charlie" ? "Chop trend" : "Win-continuation"}
            </div>
          </div>

          {/* Per-team table for chosen analyst + sport (universe view = every team
              with picks under the strategy) */}
          <div className="mb-3 px-1">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              Per-team record · {focusAnalyst === "charlie" ? "Charlie" : "Dakota"} · {sport === "mlb" ? "MLB" : "WNBA"} {season[sport]}
            </div>
          </div>
          <PerTeamFocusTable
            teams={sportData.views?.universe?.[focusAnalyst]?.teams || []}
            sport={sport}
            analystKey={focusAnalyst}
            accent={focusAnalyst === "charlie" ? CHARLIE_BLUE : DAKOTA_PURPLE}
            selectedKeys={focusAnalyst === "charlie" ? selectedKeysCharlie : selectedKeysDakota}
            optimisticOverrides={optimisticShortlist}
            onShortlist={(team, add) => toggleShortlist(focusAnalyst, team, add)}
            busyShortlist={busyShortlist}
          />

          {/* A · B · C $ comparison for focused analyst (expandable views with
              team breakdowns inside each) */}
          <div className="mt-8 mb-3 px-1">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">A · B · C universe comparison · {focusAnalyst === "charlie" ? "Charlie" : "Dakota"}</div>
            <div className="text-xs text-zinc-400 mt-1">
              Same strategy applied to <span className="text-white font-bold">A) every team</span>, <span className="text-white font-bold">B) teams above .500 on the day</span>, <span className="text-white font-bold">C) your shortlisted teams</span>. Each section's $$ total is the strategy's net P/L over that universe.
            </div>
          </div>
          <div data-testid="matrix-focused-abc">
            <AnalystColumn
              analystKey={focusAnalyst}
              accent={focusAnalyst === "charlie" ? CHARLIE_BLUE : DAKOTA_PURPLE}
              label={focusAnalyst === "charlie" ? "Charlie" : "Dakota"}
              sportKey={sport}
              sportData={sportData}
              selectedKeys={focusAnalyst === "charlie" ? selectedKeysCharlie : selectedKeysDakota}
              optimisticOverrides={optimisticShortlist}
              onShortlist={(team, add) => toggleShortlist(focusAnalyst, team, add)}
              busyShortlist={busyShortlist}
            />
          </div>

          {/* Bottom: original Charlie vs Dakota side-by-side, collapsed by default. */}
          <div className="mt-12 border-t border-white/10 pt-6" data-testid="matrix-compare-disclosure">
            <button
              onClick={() => setCompareOpen((o) => !o)}
              data-testid="matrix-compare-toggle"
              className="w-full flex items-center justify-between gap-3 px-3 py-3 bg-[#0F0F0F] border border-white/10 rounded-sm hover:border-white/30 transition-colors"
            >
              <div className="flex items-center gap-3">
                {compareOpen ? <CaretDown size={14} className="text-zinc-400" /> : <CaretRight size={14} className="text-zinc-400" />}
                <span className="text-xs uppercase tracking-widest font-bold">Compare Charlie ↔ Dakota side-by-side</span>
              </div>
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">Advanced</span>
            </button>
            {compareOpen && (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 mt-5" data-testid="matrix-columns">
                <AnalystColumn
                  analystKey="charlie"
                  accent={CHARLIE_BLUE}
                  label="Charlie"
                  sportKey={sport}
                  sportData={sportData}
                  selectedKeys={selectedKeysCharlie}
                  optimisticOverrides={optimisticShortlist}
                  onShortlist={(team, add) => toggleShortlist("charlie", team, add)}
                  busyShortlist={busyShortlist}
                />
                <AnalystColumn
                  analystKey="dakota"
                  accent={DAKOTA_PURPLE}
                  label="Dakota"
                  sportKey={sport}
                  sportData={sportData}
                  selectedKeys={selectedKeysDakota}
                  optimisticOverrides={optimisticShortlist}
                  onShortlist={(team, add) => toggleShortlist("dakota", team, add)}
                  busyShortlist={busyShortlist}
                />
              </div>
            )}
          </div>
        </>
      )}
    </motion.section>
  );
}
